import type {
  AuditEntry,
  EmailStatus,
  GraphLogFilter,
  OpsAdmin,
  OpsAuditEntry,
  OpsBugReport,
  OpsEmail,
  OpsEmailSettings,
  OpsEvent,
  OpsGraphLog,
  OpsLead,
  OpsMe,
  OpsOrg,
  OpsOrgDetail,
  OpsOverview,
  OpsSyncStatus,
  OpsSystem,
  OpsUser,
  Paged,
} from "@kontaktfix/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ApiError, api, qs } from "@/lib/api";

/**
 * Operator API calls. An expired operator session (idle timeout) sends the browser back to the
 * sign-in page instead of showing errors everywhere.
 */
async function guard<T>(promise: Promise<T>): Promise<T> {
  try {
    return await promise;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && err.code === "unauthenticated") {
      window.location.assign("/ops/login");
    }
    throw err;
  }
}

export const opsApi = {
  get: <T>(path: string) => guard(api.get<T>(path)),
  post: <T>(path: string, body?: unknown) => guard(api.post<T>(path, body)),
  patch: <T>(path: string, body: unknown) => guard(api.patch<T>(path, body)),
};

export const opsKeys = {
  all: ["ops"] as const,
  me: ["ops", "me"] as const,
  overview: ["ops", "overview"] as const,
  orgs: (q: string) => ["ops", "orgs", q] as const,
  org: (id: string) => ["ops", "org", id] as const,
  orgActivity: (id: string, page: number) => ["ops", "org-activity", id, page] as const,
  users: (q: string) => ["ops", "users", q] as const,
  sync: ["ops", "sync"] as const,
  emails: (params: object) => ["ops", "emails", params] as const,
  emailSettings: ["ops", "email-settings"] as const,
  admins: ["ops", "admins"] as const,
  audit: (page: number) => ["ops", "audit", page] as const,
  system: ["ops", "system"] as const,
  bugReports: (page: number) => ["ops", "bug-reports", page] as const,
  events: (params: object) => ["ops", "events", params] as const,
  leads: (page: number) => ["ops", "leads", page] as const,
  graph: (params: object) => ["ops", "graph", params] as const,
};

export const useOpsMe = () =>
  useQuery({ queryKey: opsKeys.me, queryFn: () => api.get<OpsMe>("/api/ops/auth/me"), retry: false });

export const useOpsOverview = () =>
  useQuery({
    queryKey: opsKeys.overview,
    queryFn: () => opsApi.get<OpsOverview>("/api/ops/overview"),
    refetchInterval: 30_000,
  });

export const useOpsOrgs = (q: string) =>
  useQuery({
    queryKey: opsKeys.orgs(q),
    queryFn: () => opsApi.get<OpsOrg[]>(`/api/ops/orgs${qs({ q })}`),
    placeholderData: keepPreviousData,
  });

export const useOpsOrg = (id: string) =>
  useQuery({
    queryKey: opsKeys.org(id),
    queryFn: () => opsApi.get<OpsOrgDetail>(`/api/ops/orgs/${id}`),
    refetchInterval: 15_000,
  });

export const useOpsOrgActivity = (id: string, page: number) =>
  useQuery({
    queryKey: opsKeys.orgActivity(id, page),
    queryFn: () => opsApi.get<Paged<AuditEntry>>(`/api/ops/orgs/${id}/activity${qs({ page })}`),
    placeholderData: keepPreviousData,
  });

export const useOpsUsers = (q: string) =>
  useQuery({
    queryKey: opsKeys.users(q),
    queryFn: () => opsApi.get<OpsUser[]>(`/api/ops/users${qs({ q })}`),
    placeholderData: keepPreviousData,
  });

export const useOpsSync = () =>
  useQuery({
    queryKey: opsKeys.sync,
    queryFn: () => opsApi.get<OpsSyncStatus>("/api/ops/sync"),
    refetchInterval: 10_000,
  });

export type EmailQuery = { page?: number; status?: EmailStatus; q?: string; orgId?: string };
export const useOpsEmails = (params: EmailQuery) =>
  useQuery({
    queryKey: opsKeys.emails(params),
    queryFn: () => opsApi.get<Paged<OpsEmail>>(`/api/ops/emails${qs(params)}`),
    placeholderData: keepPreviousData,
  });

export const useOpsEmailSettings = () =>
  useQuery({
    queryKey: opsKeys.emailSettings,
    queryFn: () => opsApi.get<OpsEmailSettings>("/api/ops/emails/settings"),
  });

export const useOpsAdmins = () =>
  useQuery({ queryKey: opsKeys.admins, queryFn: () => opsApi.get<OpsAdmin[]>("/api/ops/admins") });

export const useOpsAudit = (page: number) =>
  useQuery({
    queryKey: opsKeys.audit(page),
    queryFn: () => opsApi.get<Paged<OpsAuditEntry>>(`/api/ops/audit${qs({ page })}`),
    placeholderData: keepPreviousData,
  });

export const useOpsSystem = () =>
  useQuery({
    queryKey: opsKeys.system,
    queryFn: () => opsApi.get<OpsSystem>("/api/ops/system"),
    refetchInterval: 30_000,
  });

export const useOpsBugReports = (page: number) =>
  useQuery({
    queryKey: opsKeys.bugReports(page),
    queryFn: () => opsApi.get<Paged<OpsBugReport>>(`/api/ops/bug-reports${qs({ page })}`),
    placeholderData: keepPreviousData,
  });

export const useOpsEvents = (params: { page: number; name?: string; orgId?: string }) =>
  useQuery({
    queryKey: opsKeys.events(params),
    queryFn: () => opsApi.get<Paged<OpsEvent>>(`/api/ops/events${qs(params)}`),
    placeholderData: keepPreviousData,
  });

export const useOpsLeads = (page: number) =>
  useQuery({
    queryKey: opsKeys.leads(page),
    queryFn: () => opsApi.get<Paged<OpsLead>>(`/api/ops/leads${qs({ page })}`),
    placeholderData: keepPreviousData,
  });

export type GraphLogQuery = { orgId?: string; filter?: GraphLogFilter; minutes?: number };
export const useOpsGraphLog = (params: GraphLogQuery, live: boolean) =>
  useQuery({
    queryKey: opsKeys.graph(params),
    queryFn: () => opsApi.get<OpsGraphLog>(`/api/ops/graph${qs(params)}`),
    placeholderData: keepPreviousData,
    refetchInterval: live ? 3000 : false,
  });
